import { ENDPOINTS } from 'config/endpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { WithDataForPossibleRewrite } from 'crono-fe-common/types/withDataForPossibleRewrite';
import {
  enqueueRewriteSequenceInstanceIfNeeded,
  isProspectOwned,
} from 'crono-fe-common/utils';
import { useEnqueueAsyncRewriteJob } from 'crono-fe-common/hooks/crono-extension/gateway';
import {
  CronoExtensionStatus,
  useGetExtensionStatus,
} from '../crono-extension/useGetExtensionStatus';
import { ProspectAndSequenceInstanceIds } from 'crono-fe-common/types/prospectAndSequenceInstanceIds';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';

export type RestartStrategyInputs = {
  prospectId: string;
  strategyId: number;
  //Restarts the sequence on behalf of the contact owner, without changing ownership. Manager only
  assignToOwner?: boolean;
};

export default function useRestartStrategy() {
  const queryClient = useQueryClient();
  const { data: extensionStatus } = useGetExtensionStatus();
  const { isManager } = useRoleCapabilities();

  const restartStrategy = ({
    data: { assignToOwner, ...data },
    recipient,
  }: WithDataForPossibleRewrite<RestartStrategyInputs>) => {
    //isManager is never overridable by the caller: a non-manager sending the
    //flag is rejected with 403, so it gates the explicit value too
    const restartOnBehalfOfOwner =
      isManager && (assignToOwner ?? !isProspectOwned(recipient));

    const request: Request = {
      url: `${ENDPOINTS.strategy.restart}`,
      config: {
        method: 'post',
        data: {
          ...data,
          //The flag is omitted rather than sent as false: non-managers sending it at all are rejected with 403
          ...(restartOnBehalfOfOwner && { assignToOwner: true }),
          linkedinExtensionAvailable:
            extensionStatus?.status ===
            CronoExtensionStatus.INSTALLED_WITH_LINKEDIN_COOKIE,
        },
      },
    };

    return client(request);
  };

  const { call: enqueueRewriteJob } = useEnqueueAsyncRewriteJob();

  return useMutation<
    Response<ProspectAndSequenceInstanceIds | null>,
    CronoError,
    WithDataForPossibleRewrite<RestartStrategyInputs>
  >(restartStrategy, {
    onSettled: (result, error, { data: variables }) => {
      queryClient.invalidateQueries([ENDPOINTS.account.search.main]);
      queryClient.invalidateQueries([ENDPOINTS.prospect.main]);
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
      });
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      invalidateTimelineQueries(queryClient);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.user.analytics]);
      queryClient.invalidateQueries([ENDPOINTS.task.aggregate]);

      queryClient.invalidateQueries([ENDPOINTS.task.total]);
      queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.resumableCount,
        variables.strategyId,
      ]);

      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.main,
        variables.strategyId,
      ]);
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.details,
        variables.strategyId,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.task.linkedin.quantity]);
      queryClient.invalidateQueries([
        ENDPOINTS.assistant.rewriteSequenceInstanceInBulkProgress,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.signal.search]);
    },
    onSuccess: async (result, variables) => {
      variables.recipient.sequenceInstance = null; // if old one is present we don't want to pass it to extension
      variables.recipient.sequenceInstanceId =
        result.data.data?.sequenceInstanceId;
      //The rewrite runs in this user's extension and personalises the owner's
      //tasks, so a sequence restarted on behalf of someone else is left out
      if (isProspectOwned(variables.recipient)) {
        await enqueueRewriteSequenceInstanceIfNeeded(
          variables,
          enqueueRewriteJob,
        );
      }
    },
  });
}
